import { Button, Form, Input, message, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import authService from '../../../services/admin/auth.service';
import TotpSetupFlow from '../../profile/components/totp_setup_flow';

const LoginLayout = () => {
    const [form] = Form.useForm();
    const [otpForm] = Form.useForm();
    const [totpForm] = Form.useForm();
    const [state, setState] = useState({
        loading: false,
        step: 'credentials', // 'credentials' | 'otp' | 'totp' | 'totp_setup'
        temp_token: null,
        setup_token: null,
        totp_use_backup: false,
    });

    useEffect(() => {
        const msg = sessionStorage.getItem('totp_flash');
        if (msg) {
            sessionStorage.removeItem('totp_flash');
            message.warning({ content: msg, duration: 8 });
        }
    }, []);

    const onLogin = async () => {
        setState(s => ({ ...s, loading: true }));
        const { username, password } = form.getFieldsValue();
        const loginResponse = await authService.login({ username, password }).catch(err => {
            const status = err?.response?.status;
            if (status === 401) {
                message.error({ content: 'Invalid Credentials', duration: 3 });
            } else if (status === 400) {
                message.error({ content: err?.response?.data?.message || 'No email linked to this account', duration: 4 });
            } else {
                notification.error({ message: 'Oops, something went wrong', description: 'Please contact support' });
            }
            setState(s => ({ ...s, loading: false }));
        });

        // Priority order: require_totp_setup first (also has a token field), then require_totp, then email OTP
        if (loginResponse?.require_totp_setup === true) {
            setState(s => ({ ...s, loading: false, step: 'totp_setup', setup_token: loginResponse.setup_token }));
        } else if (loginResponse?.require_totp === true) {
            setState(s => ({ ...s, loading: false, step: 'totp', temp_token: loginResponse.temp_token, totp_use_backup: false }));
        } else if (loginResponse?.temp_token) {
            setState(s => ({ ...s, loading: false, step: 'otp', temp_token: loginResponse.temp_token }));
            message.success({ content: loginResponse.message || 'OTP sent to your email', duration: 4 });
        } else {
            setState(s => ({ ...s, loading: false }));
        }
    };

    const onVerifyOtp = async () => {
        setState(s => ({ ...s, loading: true }));
        const { otp_code } = otpForm.getFieldsValue();
        const verifyResponse = await authService.verifyOtp({ temp_token: state.temp_token, otp_code }).catch(err => {
            const msg = err?.response?.data?.message || '';
            if (msg === 'Verification code expired') {
                message.error({ content: 'OTP has expired. Please login again.', duration: 4 });
                setState(s => ({ ...s, loading: false, step: 'credentials', temp_token: null }));
                otpForm.resetFields();
            } else if (msg === 'Verification code already used') {
                message.error({ content: 'OTP already used. Please login again.', duration: 4 });
                setState(s => ({ ...s, loading: false, step: 'credentials', temp_token: null }));
                otpForm.resetFields();
            } else {
                message.error({ content: 'Invalid verification code', duration: 3 });
                setState(s => ({ ...s, loading: false }));
            }
        });

        if (verifyResponse?.token) {
            window.location.reload(false);
        } else if (verifyResponse) {
            setState(s => ({ ...s, loading: false }));
        }
    };

    const onVerifyTotp = async () => {
        setState(s => ({ ...s, loading: true }));
        const { code } = totpForm.getFieldsValue();
        const params = state.totp_use_backup
            ? { temp_token: state.temp_token, backup_code: code }
            : { temp_token: state.temp_token, totp_code: code };

        const verifyResponse = await authService.verifyOtp(params).catch(err => {
            const msg = err?.response?.data?.message || '';
            const tokenExpiredMessages = [
                'Invalid or expired session. Please log in again.',
                'Token already used. Please log in again.',
                'Session expired. Please log in again.',
            ];
            if (tokenExpiredMessages.includes(msg)) {
                message.error({ content: msg, duration: 5 });
                setState(s => ({ ...s, loading: false, step: 'credentials', temp_token: null }));
                form.resetFields();
                totpForm.resetFields();
            } else {
                message.error({ content: msg || 'Invalid code', duration: 4 });
                setState(s => ({ ...s, loading: false }));
            }
        });

        if (verifyResponse?.token) {
            window.location.reload(false);
        } else if (verifyResponse) {
            setState(s => ({ ...s, loading: false }));
        }
    };

    const onBackToLogin = () => {
        setState(s => ({ ...s, step: 'credentials', temp_token: null, setup_token: null, loading: false }));
        form.resetFields();
        otpForm.resetFields();
        totpForm.resetFields();
    };

    const switchTotpMode = (use_backup) => {
        totpForm.resetFields();
        setState(s => ({ ...s, totp_use_backup: use_backup }));
    };

    return (
        <>
            <div className='content' style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                <div>
                    <div>
                        <span
                            className='ticket-label ticket-title h1'
                            style={{ fontWeight: 'bold', fontSize: 24 }}
                        >
                            {state.step === 'credentials' ? 'Enter your credentials'
                                : state.step === 'otp' ? 'Enter OTP'
                                : state.step === 'totp_setup' ? 'Set Up Two-Factor Authentication'
                                : 'Two-Factor Authentication'}
                        </span>
                    </div>

                    {state.step === 'credentials' && (
                        <div style={{ marginTop: 42 }}>
                            <Form form={form} onFinish={onLogin}>
                                <Form.Item name={'username'}>
                                    <Input size='large' placeholder='Username' />
                                </Form.Item>
                                <Form.Item name={'password'}>
                                    <Input size='large' placeholder='Password' type='password' />
                                </Form.Item>
                                <Form.Item>
                                    <Button loading={state.loading} htmlType='submit' type='primary' size='large' style={{ width: '100%' }}>
                                        Sign In
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>
                    )}

                    {state.step === 'otp' && (
                        <div style={{ marginTop: 42 }}>
                            <p style={{ marginBottom: 24, color: '#666' }}>
                                An OTP has been sent to your email. Enter it below to continue.
                            </p>
                            <Form form={otpForm} onFinish={onVerifyOtp}>
                                <Form.Item name={'otp_code'}>
                                    <Input size='large' placeholder='OTP Code' maxLength={6} />
                                </Form.Item>
                                <Form.Item>
                                    <Button loading={state.loading} htmlType='submit' type='primary' size='large' style={{ width: '100%' }}>
                                        Verify
                                    </Button>
                                </Form.Item>
                            </Form>
                            <Button type='link' onClick={onBackToLogin} style={{ paddingLeft: 0 }}>
                                Back to login
                            </Button>
                        </div>
                    )}

                    {state.step === 'totp_setup' && (
                        <div style={{ marginTop: 42 }}>
                            <TotpSetupFlow
                                setup_token={state.setup_token}
                                on_success={() => window.location.reload(false)}
                                on_cancel={onBackToLogin}
                            />
                        </div>
                    )}

                    {state.step === 'totp' && (
                        <div style={{ marginTop: 42 }}>
                            <p style={{ marginBottom: 24, color: '#666' }}>
                                {state.totp_use_backup
                                    ? 'Enter one of your backup codes to continue.'
                                    : 'Enter the 6-digit code from your authenticator app to continue.'}
                            </p>
                            <Form form={totpForm} onFinish={onVerifyTotp}>
                                <Form.Item name={'code'}>
                                    {state.totp_use_backup ? (
                                        <Input
                                            key="backup"
                                            autoFocus
                                            size='large'
                                            placeholder='XXXX-XXXX-XXXX'
                                            maxLength={14}
                                            autoComplete='one-time-code'
                                        />
                                    ) : (
                                        <Input
                                            key="totp"
                                            autoFocus
                                            size='large'
                                            placeholder='6-digit code'
                                            maxLength={6}
                                            inputMode='numeric'
                                            autoComplete='one-time-code'
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                totpForm.setFieldValue('code', val);
                                            }}
                                        />
                                    )}
                                </Form.Item>
                                <Form.Item>
                                    <Button loading={state.loading} htmlType='submit' type='primary' size='large' style={{ width: '100%' }}>
                                        Verify
                                    </Button>
                                </Form.Item>
                            </Form>
                            {state.totp_use_backup ? (
                                <Button type='link' onClick={() => switchTotpMode(false)} style={{ paddingLeft: 0 }}>
                                    Use authenticator code instead
                                </Button>
                            ) : (
                                <Button type='link' onClick={() => switchTotpMode(true)} style={{ paddingLeft: 0 }}>
                                    Use a backup code instead
                                </Button>
                            )}
                            <div>
                                <Button type='link' onClick={onBackToLogin} style={{ paddingLeft: 0 }}>
                                    Back to login
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LoginLayout;